/**
 * Settings — appearance and profile.
 *
 * **No Save button.** Appearance applies the moment it is picked and persists
 * on its own (debounced). An earlier version applied the change *and* showed a
 * Save button, which was both redundant and actively misleading: the change had
 * already happened, and the button sat there implying it had not. Worse, it
 * disabled itself the instant anything changed, so it could not be pressed even
 * if you wanted to. A quiet status line replaces it.
 *
 * The profile section shows the Entra-owned fields as read-only rather than
 * hiding them: "why can't I change my email here" is a question better answered
 * on the page than in a support ticket. `display_name` is the one field the
 * platform genuinely owns locally, and it is edited on the profile page that
 * already handles it.
 */

import {
  Alert,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconInfoCircle } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { useMe } from "@/features/users/useMe";
import { useUserSettings } from "@/features/settings/UserSettingsProvider";
import type { SettingsOut } from "@/shared/api/settings";

const FONT_SIZE_OPTIONS = [
  { label: "Small", value: "sm" },
  { label: "Medium", value: "md" },
  { label: "Large", value: "lg" },
  { label: "Extra large", value: "xl" },
];

const FONT_FAMILY_OPTIONS = [
  { label: "System default", value: "system" },
  { label: "Inter", value: "inter" },
  { label: "Serif", value: "serif" },
  { label: "Monospace", value: "mono" },
];

function SaveStatusLine({ status }: { status: ReturnType<typeof useUserSettings>["status"] }) {
  if (status === "saving") {
    return (
      <Group gap={6}>
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Saving…
        </Text>
      </Group>
    );
  }
  if (status === "saved") {
    return (
      <Group gap={6}>
        <IconCheck size={14} color="var(--mantine-color-green-6)" />
        <Text size="xs" c="dimmed">
          Saved — these follow you on any device.
        </Text>
      </Group>
    );
  }
  if (status === "error") {
    return (
      <Group gap={6}>
        <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />
        <Text size="xs" c="dimmed">
          Applied on this device, but could not be saved to your account.
        </Text>
      </Group>
    );
  }
  return null;
}

export function SettingsPage() {
  const { settings, loading, status, update } = useUserSettings();
  const { data: me } = useMe();

  const change = (patch: Partial<SettingsOut>) => update({ ...settings, ...patch });

  return (
    <Stack gap="lg" maw={720}>
      <Title order={3}>Settings</Title>

      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <div>
            <Text fw={600}>Appearance</Text>
            <Text size="sm" c="dimmed">
              Changes apply straight away and save automatically.
            </Text>
          </div>

          <Select
            label="Text size"
            data={FONT_SIZE_OPTIONS}
            value={settings.font_size}
            onChange={(value) => value && change({ font_size: value })}
            disabled={loading}
            allowDeselect={false}
          />

          <Select
            label="Font"
            data={FONT_FAMILY_OPTIONS}
            value={settings.font_family}
            onChange={(value) => value && change({ font_family: value })}
            disabled={loading}
            allowDeselect={false}
          />

          {/* The setting is about reading text, so show text at the chosen size
              rather than asking the user to judge it from the controls. */}
          <Card withBorder radius="sm" padding="sm" bg="var(--mantine-color-body)">
            <Text size="xs" c="dimmed" mb={4}>
              Preview
            </Text>
            <Title order={4}>Release risk assessment</Title>
            <Text size="sm">The quick brown fox jumps over the lazy dog — 0123456789.</Text>
            <Text size="xs" c="dimmed">
              Smaller supporting text, as used on cards and captions.
            </Text>
          </Card>

          <SaveStatusLine status={status} />
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <div>
            <Text fw={600}>Profile</Text>
            <Text size="sm" c="dimmed">
              Your identity comes from your organisation&apos;s Microsoft directory.
            </Text>
          </div>

          <Alert icon={<IconInfoCircle size={16} />} variant="light">
            Name, email, and roles are managed in your organisation&apos;s directory and cannot be
            changed here. To edit your display name within this application, use{" "}
            <RouterLink to="/me">your profile page</RouterLink>.
          </Alert>

          <TextInput label="Email" value={me?.email ?? ""} disabled readOnly />
          <TextInput label="Display name" value={me?.display_name ?? ""} disabled readOnly />
          <TextInput label="Roles" value={(me?.roles ?? []).join(", ")} disabled readOnly />
        </Stack>
      </Card>
    </Stack>
  );
}
